"use client"

import React, { ChangeEvent, useEffect, useMemo, useState } from "react"
import Link from "next/link"
import { useSearchParams } from "next/navigation"
import { updateBasketItemQuantityAction } from "@/actions/basketActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { MinusIcon, PlusIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import { generateProductLink } from "@/lib/link-utils/link-utils"
import { getIsoLocaleFromSbLocale } from "@/lib/locale/locale-common-utils"
import { BasketLineItemData } from "@/lib/storefront/basket/basket"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { determinePrefixedProductId } from "@/lib/storefront/product/productHelper"
import { cn } from "@/components/ui/helpers/utils"
import { BasketLineItemActions } from "@/components/basket/overview/BasketLineItemActions"
import { BasketLineItemPriceInfo } from "@/components/basket/overview/BasketLineItemPriceInfo"
import BasicCard from "@/components/common/BasicCard"
import ImageWithFallback from "@/components/storyblok/common/ImageWithFallback"

function OptionalLink(props: { link: string; shouldRenderLink: boolean; children: React.ReactNode }) {
  return (
    <>
      {props.shouldRenderLink ? (
        <Link prefetch={false} className={"cursor-pointer"} href={props.link}>
          {props.children}
        </Link>
      ) : (
        <span>{props.children}</span>
      )}
    </>
  )
}

export function BasketLineItemCard(props: {
  isB2B: boolean
  lineItem: BasketLineItemData
  sbLocale: string
  preventLinkToProductDetails: boolean
}) {
  const t = useTranslations("storefront")

  const productDataAddedToBasket: ReducedProductData = props.lineItem.product
  const [quantityIsAboutToChange, setQuantityIsAboutToChange] = useState<boolean>(false)
  const { setBasketData, showAllPrices } = useUbStorefrontState()

  const [quantity, setQuantity] = useState(props.lineItem.quantity)

  const variationAttributesAsString = useMemo(() => {
    const attributeArray: string[] | undefined = props.lineItem.product?.variableVariationAttributes?.map(
      (variationAttribute) => {
        return variationAttribute.name + ": " + variationAttribute.value
      }
    )

    return attributeArray?.join(", ")
  }, [props.lineItem])

  const [inputQuantity, setInputQuantity] = useState<string>(props.lineItem.quantity + "")

  const searchParams = useSearchParams()
  const variantLink = generateProductLink(
    props.lineItem.product.title,
    props.lineItem.product.id,
    searchParams,
    props.sbLocale
  )

  useEffect(() => {
    setInputQuantity(props.lineItem.quantity + "")
  }, [props.lineItem])

  function updateBasketItemQuantityHandler(newQuantity: number) {
    if (productDataAddedToBasket) {
      setQuantityIsAboutToChange(true)
      updateBasketItemQuantityAction(props.lineItem, productDataAddedToBasket, newQuantity).then((basketData) => {
        setQuantityIsAboutToChange(false)
        if (basketData) {
          setBasketData(basketData)
        }
      })
    }
  }

  function handleQuantityIncrease() {
    const newQuantity = quantity + (productDataAddedToBasket?.stepOrderQuantity ?? 1)
    setInputQuantity(newQuantity + "")
    setQuantity(newQuantity)
    updateBasketItemQuantityHandler(newQuantity)
  }

  function handleQuantityDecrease() {
    if (quantity > (productDataAddedToBasket?.stepOrderQuantity ?? 1)) {
      const newQuantity = quantity - (productDataAddedToBasket?.stepOrderQuantity ?? 1)
      setInputQuantity(newQuantity + "")
      setQuantity(newQuantity)
      updateBasketItemQuantityHandler(newQuantity)
    }
  }

  function updateQuantity(wantedQuantity: number) {
    console.log("Update quantity: " + wantedQuantity)
    if (!(wantedQuantity > 0)) {
      return
    }

    const quantitySteps = productDataAddedToBasket?.stepOrderQuantity
    const minimumOrderQuantity = productDataAddedToBasket?.minOrderQuantity
    if (quantitySteps !== undefined && minimumOrderQuantity !== undefined) {
      const newAddableQuantity = wantedQuantity - (wantedQuantity % quantitySteps)
      const newQuantity = Math.max(minimumOrderQuantity, newAddableQuantity)
      setQuantity(newQuantity)
      setInputQuantity(newQuantity + "")
      updateBasketItemQuantityHandler(newQuantity)
    }
  }

  const changeHandler = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.trim().toLowerCase() //.replace(/\D/,'')
    setInputQuantity(inputValue)
  }

  const blurHandler = () => {
    //console.log("input blurred")
    const intQuantity = parseInt(inputQuantity)
    updateQuantity(intQuantity)
  }

  return (
    <>
      <div className={cn("min-h-32 rounded-none border-0 border-gray-300 p-0 py-2 sm:p-0 sm:py-4")}>
        <pre className={"hidden text-xs"}>{JSON.stringify(props.lineItem, undefined, 2)}</pre>
        <div className={"grid grid-cols-12 gap-2"}>
          <div className={"col-span-3 lg:col-span-2"}>
            <div className={"flex h-auto flex-col justify-between gap-2"}>
              <OptionalLink link={variantLink} shouldRenderLink={!props.preventLinkToProductDetails}>
                <BasicCard
                  className={
                    "flex aspect-square max-h-24 min-h-1 items-center justify-center p-1 sm:p-1 lg:max-h-52 lg:p-2"
                  }
                >
                  <ImageWithFallback
                    unoptimized={true}
                    className={"aspect-square h-full max-h-24 object-contain lg:max-h-52 "}
                    src={props.lineItem.product.image ?? ""}
                    width={260}
                    height={260}
                    alt={props.lineItem.product.title}
                  />
                </BasicCard>
              </OptionalLink>
              <div>{/*  <BasketLineItemDeliveryInfo productData={props.lineItem.product}/>*/}</div>
            </div>
          </div>
          <div className={"col-span-9 lg:col-span-6"}>
            <div className={"flex h-full flex-col justify-between gap-2"}>
              <div>
                <p className={"text-xs font-bold text-muted-foreground"}>{props.lineItem.product.brand}</p>
                <OptionalLink link={variantLink} shouldRenderLink={!props.preventLinkToProductDetails}>
                  <p className={"text-sm font-bold"}>{props.lineItem.product.title}</p>
                </OptionalLink>
                <OptionalLink link={variantLink} shouldRenderLink={!props.preventLinkToProductDetails}>
                  <p className={"text-xs"}>
                    {t("product.article_number")}:{" " + determinePrefixedProductId(props.lineItem.product.id)}
                  </p>
                </OptionalLink>
                <div className={"flex flex-row"}>
                  <p className={"text-xs font-bold text-muted-foreground"}>{variationAttributesAsString}</p>
                </div>
                {(productDataAddedToBasket.minOrderQuantity ?? 1) > 1 && (
                  <div className={"flex flex-row"}>
                    <p className={"text-xs font-bold text-muted-foreground"}>{productDataAddedToBasket.minOrderInfo}</p>
                  </div>
                )}
              </div>
              {/*QuantityChange*/}
              <div className={"flex flex-row justify-end gap-2 lg:justify-start"}>
                <div>
                  <div
                    className={cn(
                      "flex w-auto min-w-32 shrink-0 flex-row items-center justify-center rounded-lg bg-gray-100 p-0 px-2 lg:w-auto"
                    )}
                  >
                    {!props.lineItem.originalLineItem?.quantityFixed ? (
                      <>
                        <button
                          className={cn("w-auto rounded-full py-1 pl-4 pr-2")}
                          onClick={handleQuantityDecrease}
                          disabled={
                            quantityIsAboutToChange ||
                            inputQuantity === productDataAddedToBasket.minOrderQuantity?.toString()
                          }
                          aria-label={t("product.quantity_decrease")}
                        >
                          <MinusIcon
                            className={cn("size-5", {
                              "text-muted-foreground opacity-20":
                                inputQuantity === productDataAddedToBasket.minOrderQuantity?.toString(),
                            })}
                          />
                        </button>
                        <input
                          type="text"
                          pattern="[0-9]*"
                          aria-label={t("product.quantity")}
                          disabled={quantityIsAboutToChange}
                          className={cn(
                            "h-9 w-12 border-transparent bg-transparent p-0 py-1 text-center text-lg font-bold focus:rounded-md focus:border-2 focus:border-blue-400 focus:outline-none focus:ring-transparent"
                          )}
                          value={inputQuantity}
                          onChange={changeHandler}
                          onBlur={blurHandler}
                          onKeyDown={(event) => {
                            if (
                              !(
                                /[0-9]/.test(event.key) ||
                                event.code === "Backspace" ||
                                event.code === "Delete" ||
                                event.code === "Delete" ||
                                event.code === "ArrowLeft" ||
                                event.code === "ArrowRight"
                              )
                            ) {
                              event.preventDefault()
                            }
                          }}
                        />
                        <button
                          className={cn("w-auto rounded-full py-1 pl-2 pr-4")}
                          onClick={handleQuantityIncrease}
                          disabled={quantityIsAboutToChange}
                          aria-label={"product.quantity_increase"}
                        >
                          <PlusIcon className={"size-5"} />
                        </button>
                      </>
                    ) : (
                      <>
                        <p
                          className={cn(
                            "h-9 w-32 border-transparent bg-transparent px-4 py-1 text-center text-lg font-bold focus:rounded-md focus:border-2 focus:border-blue-400 focus:outline-none focus:ring-transparent"
                          )}
                        >
                          {inputQuantity}
                        </p>
                      </>
                    )}
                  </div>
                </div>

                <BasketLineItemActions lineItem={props.lineItem} />
              </div>
            </div>
          </div>
          <div className={"col-span-12 lg:col-span-4"}>
            <BasketLineItemPriceInfo
              isoLocale={getIsoLocaleFromSbLocale(props.sbLocale)}
              isB2B={props.isB2B}
              lineItem={props.lineItem}
              showAllPrices={showAllPrices}
            />
          </div>
        </div>
      </div>
    </>
  )
}
